import React, { useEffect, useRef, useState } from 'react';
import classes from './PurchaseCard.module.scss';
import ShippingMap from './ShippingMap';
import { getMyDealerPurchaseTracking } from '../../services/dealerGarage';

// AIS times arrive as "2026-10-13 08:12:00 UTC" (sometimes without the zone)
// and are UTC either way; fetchedAt is a regular ISO timestamp.
const parseAisTime = (value) => {
    if (!value) return null;
    const text = String(value);
    const ais = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(text)
        ? null
        : /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/.exec(text);
    const date = ais
        ? new Date(Date.UTC(+ais[1], +ais[2] - 1, +ais[3], +ais[4], +ais[5], +(ais[6] || 0)))
        : new Date(text);
    return Number.isNaN(date.getTime()) ? null : date;
};

const formatStamp = (value) => {
    const date = parseAisTime(value);
    if (!date) return '';
    return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/**
 * The ship on a map with its speed, destination and ETA, or why there is no
 * position. `tracking` is { status: 'idle' | 'loading' | 'ready' | 'error',
 * data: the position or null, voyage: where the ship says it is going }.
 *
 * The staff purchase panel shows this too, so staff see what the customer sees.
 */
export const ShipTrackingView = ({ tracking, vesselName }) => {
    const position = tracking.data;
    const voyage = tracking.status === 'ready' ? tracking.voyage : null;
    const hasPosition = !!(position && Number.isFinite(position.latitude) && Number.isFinite(position.longitude));
    // reportedAt is when the ship itself last reported; fetchedAt only says
    // when we last asked, so it is the fallback.
    const updated = hasPosition ? formatStamp(position.reportedAt) || formatStamp(position.fetchedAt) : '';
    // Mid-ocean the ship is out of range of shore receivers, so its last
    // position can be days old; say so rather than let the map look current.
    const reported = hasPosition ? parseAisTime(position.reportedAt) : null;
    const quiet = !!(reported && Date.now() - reported.getTime() > 24 * 60 * 60 * 1000);

    let placeholder = 'Locating the ship…';
    if (tracking.status === 'error') placeholder = 'The ship\'s position could not be loaded right now. Please try again later.';
    else if (tracking.status === 'ready') placeholder = voyage
        ? 'The ship is at sea, out of range of shore receivers. Its position will update as it nears land.'
        : 'Position not available yet';

    return hasPosition ? (
        <>
            <div className={classes.mapFrame}>
                <ShippingMap
                    coordinates={[position.latitude, position.longitude]}
                    shipDetails={{ name: position.name || vesselName }}
                />
            </div>
            <div className={classes.trackingFacts}>
                {Number.isFinite(position.speed) && <span>Speed {position.speed} knots</span>}
                {position.destination && <span>Destination {position.destination}</span>}
                {position.eta && formatStamp(position.eta) && <span>Ship&apos;s ETA {formatStamp(position.eta)}</span>}
                {updated && <span>Last updated {updated}</span>}
                {quiet && <span>At sea since then: the position updates as the ship nears land</span>}
            </div>
        </>
    ) : (
        <div className={classes.mapPlaceholder} role="status">
            <i className="fas fa-ship" aria-hidden="true"></i>
            {placeholder}
            {voyage && (
                <div className={classes.trackingFacts}>
                    {voyage.destination && <span>Destination {voyage.destination}</span>}
                    {voyage.eta && formatStamp(voyage.eta) && <span>Ship&apos;s ETA {formatStamp(voyage.eta)}</span>}
                    {voyage.lastReportAt && formatStamp(voyage.lastReportAt) && <span>Last report {formatStamp(voyage.lastReportAt)}</span>}
                </div>
            )}
        </div>
    );
};

/**
 * Where the ship is now, in the customer's garage. Fetched only once the panel
 * scrolls near the viewport - a lookup can cost a paid vessel-API call (the
 * server caches each ship for an hour), and most visits never scroll to an
 * older purchase.
 *
 * `load(trackId)` resolves to { message: 'Success', data: position | null,
 * voyage }; the default asks for a dealer-stock purchase by its id.
 */
const ShipTracking = ({ trackId, vesselName, load = getMyDealerPurchaseTracking }) => {
    const panelRef = useRef(null);
    const [visible, setVisible] = useState(false);
    const [tracking, setTracking] = useState({ status: 'idle', data: null });

    useEffect(() => {
        const node = panelRef.current;
        if (!node || typeof IntersectionObserver === 'undefined') {
            setVisible(true);
            return undefined;
        }
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                setVisible(true);
                observer.disconnect();
            }
        }, { rootMargin: '200px' });
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!visible) return undefined;
        let active = true;
        setTracking({ status: 'loading', data: null });
        load(trackId)
            .then((res) => {
                if (!active) return;
                if (res?.message !== 'Success') throw new Error(res?.message || 'Tracking unavailable');
                // voyage: where the ship says it is going, there even when it
                // is out of range of shore receivers with no position.
                setTracking({ status: 'ready', data: res.data || null, voyage: res.voyage || null });
            })
            .catch(() => {
                if (active) setTracking({ status: 'error', data: null });
            });
        return () => { active = false; };
    }, [visible, trackId, load]);

    return (
        <div ref={panelRef} className={classes.tracking}>
            <ShipTrackingView tracking={tracking} vesselName={vesselName} />
        </div>
    );
};

export default ShipTracking;
